import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageBanner from '@/components/base/PageBanner';
import SearchBar from '@/components/feature/SearchBar';
import Pagination from '@/components/base/Pagination';
import FavoriteButton from '@/components/base/FavoriteButton';
import { useSeo } from '@/hooks/useSeo';
import { formatDate } from '@/utils/date';
import { newsList } from '@/mocks/news';
import { eventsList } from '@/mocks/events';
import { charactersList } from '@/mocks/characters';
import { guidesList } from '@/mocks/guides';

interface SearchResult {
  id: string;
  title: string;
  desc: string;
  cover: string;
  to: string;
  type: 'news' | 'event' | 'character' | 'guide';
  date: string;
  favId: string;
  tags: string[];
}

const TYPE_TABS = [
  { value: 'all', label: '全部' },
  { value: 'news', label: '资讯' },
  { value: 'event', label: '活动' },
  { value: 'character', label: '角色' },
  { value: 'guide', label: '攻略' },
];

const TYPE_LABELS: Record<string, string> = { news: '资讯', event: '活动', character: '角色', guide: '攻略' };

const PAGE_SIZE = 8;

function buildResults(): SearchResult[] {
  const items: SearchResult[] = [];
  newsList.forEach((item) =>
    items.push({
      id: item.id,
      title: item.title,
      desc: item.excerpt,
      cover: item.cover,
      to: `/news/${item.id}`,
      type: 'news',
      date: item.date,
      favId: `news-${item.id}`,
      tags: item.tags,
    }),
  );
  eventsList.forEach((item) =>
    items.push({
      id: item.id,
      title: item.title,
      desc: item.intro,
      cover: item.cover,
      to: `/events/${item.id}`,
      type: 'event',
      date: item.start,
      favId: `event-${item.id}`,
      tags: item.tags,
    }),
  );
  charactersList.forEach((item) =>
    items.push({
      id: item.id,
      title: `${item.name} · 角色攻略`,
      desc: item.intro,
      cover: item.avatar,
      to: `/characters/${item.id}`,
      type: 'character',
      date: item.releaseDate,
      favId: `char-${item.id}`,
      tags: [item.elementName, item.roleName, item.weapon],
    }),
  );
  guidesList.forEach((item) =>
    items.push({
      id: item.id,
      title: item.title,
      desc: item.summary,
      cover: item.cover,
      to: `/guides/${item.id}`,
      type: 'guide',
      date: item.date,
      favId: `guide-${item.id}`,
      tags: item.tags,
    }),
  );
  return items;
}

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get('q') ?? '').trim();
  const [type, setType] = useState('all');
  const [sort, setSort] = useState('relevance');
  const [page, setPage] = useState(1);

  useSeo({
    title: keyword ? `“${keyword}” 搜索结果 – 原神官方游戏站` : '搜索结果 – 原神官方游戏站',
    description: keyword
      ? `与“${keyword}”相关的原神资讯、活动、角色与攻略搜索结果汇总。`
      : '搜索原神资讯、活动、角色与攻略内容。',
    keywords: keyword ? `原神,${keyword},原神搜索` : '原神搜索',
    canonicalPath: '/search',
  });

  const allResults = useMemo(buildResults, []);

  const matched = useMemo(() => {
    const lower = keyword.toLowerCase();
    if (!lower) return [];
    const filtered = allResults.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.desc.toLowerCase().includes(lower) ||
        item.tags.some((tag) => tag.toLowerCase().includes(lower)),
    );
    if (sort === 'date') {
      return [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return [...filtered].sort((a, b) => {
      const aTitle = a.title.toLowerCase().includes(lower) ? 0 : 1;
      const bTitle = b.title.toLowerCase().includes(lower) ? 0 : 1;
      return aTitle - bTitle;
    });
  }, [allResults, keyword, sort]);

  const tabbed = useMemo(() => matched.filter((item) => (type === 'all' ? true : item.type === type)), [matched, type]);

  const counts = useMemo(() => {
    const base: Record<string, number> = { all: matched.length, news: 0, event: 0, character: 0, guide: 0 };
    matched.forEach((item) => {
      base[item.type] += 1;
    });
    return base;
  }, [matched]);

  const totalPages = Math.max(1, Math.ceil(tabbed.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = tabbed.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateType = (value: string) => {
    setType(value);
    setPage(1);
  };

  const recommendations = allResults.filter((item) => item.type === 'guide').slice(0, 3);

  return (
    <div>
      <PageBanner
        kicker="SEARCH"
        title={keyword ? `“${keyword}” 的搜索结果` : '搜索内容'}
        subtitle="全站搜索支持资讯、活动、角色与攻略内容，可按分类切换并切换排序方式。"
        crumbs={[{ label: '首页', to: '/' }, { label: '搜索结果' }]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <SearchBar defaultValue={keyword} />
        </div>

        <div className="mt-8 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => updateType(tab.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                    type === tab.value
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {tab.label}
                  <span className="ml-1.5 text-xs opacity-70">{counts[tab.value] ?? 0}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序</span>
              <div className="flex rounded-full bg-background-100 p-1">
                {[
                  { value: 'relevance', label: '按相关度' },
                  { value: 'date', label: '按时间' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSort(option.value);
                      setPage(1);
                    }}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs font-label transition-colors ${
                      sort === option.value ? 'bg-background-50 text-primary-700' : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-foreground-500">
            {keyword ? (
              <>
                关键词 “<span className="font-medium text-primary-700">{keyword}</span>” 共找到{' '}
                <span className="font-medium text-primary-700">{tabbed.length}</span> 条结果
              </>
            ) : (
              '请输入关键词开始搜索'
            )}
          </p>
        </div>

        {paged.length > 0 ? (
          <div className="mt-8 space-y-4">
            {paged.map((item) => (
              <article key={`${item.type}-${item.id}`} className="gs-card-hover group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-3 sm:flex-row">
                <Link to={item.to} className="block w-full shrink-0 cursor-pointer overflow-hidden rounded-lg sm:w-52">
                  <div className="h-36 w-full sm:h-32">
                    <img src={item.cover} alt={item.title} title={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                </Link>
                <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-label text-secondary-900">{TYPE_LABELS[item.type]}</span>
                      <span className="inline-flex items-center gap-1 text-foreground-500">
                        <i className="ri-calendar-line"></i>
                        {formatDate(item.date)}
                      </span>
                    </div>
                    <h2 className="mt-2 line-clamp-2 font-heading text-base font-bold text-foreground-950">
                      <Link to={item.to} className="cursor-pointer transition-colors hover:text-primary-700">
                        {item.title}
                      </Link>
                    </h2>
                    <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{item.desc}</p>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {item.tags.slice(0, 2).map((tag) => (
                        <Link
                          key={tag}
                          to={`/search?q=${encodeURIComponent(tag)}`}
                          className="cursor-pointer rounded-full bg-background-100 px-2 py-0.5 text-[11px] text-foreground-500 transition-colors hover:text-primary-700"
                        >
                          #{tag}
                        </Link>
                      ))}
                    </div>
                    <FavoriteButton id={item.favId} />
                  </div>
                </div>
              </article>
            ))}
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        ) : (
          <div className="mt-8">
            <div className="rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-base font-bold text-foreground-900">
                {keyword ? '没有找到相关结果' : '输入关键词开始搜索'}
              </p>
              <p className="mt-1.5 text-sm text-foreground-500">
                {keyword ? '试试更换关键词，或看看下面的热门推荐内容。' : '可以搜索角色名、版本号、活动名称或攻略主题。'}
              </p>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                <a href="#popular-recommend">热门推荐</a>
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {recommendations.map((item) => (
                  <Link key={item.id} to={item.to} className="gs-card-hover group overflow-hidden rounded-xl border border-background-200 bg-background-50">
                    <div className="h-32 w-full overflow-hidden">
                      <img src={item.cover} alt={item.title} title={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-3.5">
                      <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">{item.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}